import {
    Assessment as APIAssessment,
    AssessmentSchedule as APIAssessmentSchedule,
    Issue as APIIssue,
    LinkRegistry as APILinkRegistry,
    Location as APILocation,
    Property as APIProperty,
    ResourceAssessmentScheduleAssociation as APIResourceAssessmentScheduleAssociation,
    Dimension,
    ExternalReportSubmission,
    ExternalReportSubmissionIssue,
    ExternalReportType,
    ImageSource,
    Location,
    MeasurementInput,
    NotificationRecipient,
    RecurrenceInput,
    SubmitQuickQuoteInput,
    Tag
} from "../../API";
import {
    Assessment,
    AssessmentSchedule,
    Issue,
    NotificationResourceType,
    NotificationType,
    PermissionResourceType,
    ResourceAssessmentScheduleAssociation,
} from "../../models";
import {
    AssessmentSubmissionOrchestrator,
    StagedImage
} from "@tenera/assessment_submission_client";
import {
    Auth,
    API as GraphQLAPI
} from "aws-amplify";
import {
    Box,
    LinearProgress,
    Typography
} from "@mui/material";
import { IMAGE_QUERY_KEY, useImagesByPropertyId } from "./data/hooks/useImages";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    useMutation,
    useQuery,
    useQueryClient
} from "react-query";

import { ContactFormData } from "../contact/ContactForm";
import { ContactFormDialog } from "../contact/ContactFormDialog";
import { DATA_LOAD_TIME_METRIC_NAME } from "../../lib/metrics/RUMMetricConstants";
import { GraphQLIssueDAOFactory } from "../../lib/issue/dao/graphql/GraphQLIssueDAOFactory";
import { GraphQLLinkRegistryDAOFactory } from "../../lib/linkRegistry/dao/GraphQLLinkRegistryDAOFactory";
import GraphQLLocationDAOFactory from "../../lib/location/dao/graphql/GraphQLLocationDAOFactory";
import { ImageEntity } from "../../lib/dashboard/types/ImageEntity";
import { Resource } from "../../componentsV2/permission/type/Resource";
import { ResourceNotificationSettingDraft } from "../../lib/dashboard/types/ResourceNotificationSettingDraft";
import { TeneraPropertyDashboard } from "./pages/TeneraPropertyDashboard";
import { awsRum } from "../../App";
import { graphQLAssessmentScheduleDAO } from "../../lib/assessment/schedule/GraphQLAssessmentScheduleDAO";
import { graphQLContactDAO } from "../../lib/contact/dao/GraphQLContactDAO";
import { graphQLQuickQuoteClient } from "../../lib/quickQuote/client/GraphQLSubmitQuickQuoteClient";
import { graphQLQuickQuoteDAO } from "../../lib/quickQuote/dao/GraphQLQuickQuoteDAO";
import { graphQLResourceAssessmentScheduleAssociationDAO } from "../../lib/assessment/schedule/GraphQLResourceAssessmentScheduleAssociationDAO";
import { graphQLResourceNotificationSettingDAO } from "../../lib/notification/settings/dao/GraphQLResourceNotificationSettingDAO";
import { graphQLTagDAO } from "../../lib/tag/dao/GraphQLTagDAO";
import { isMobile } from "react-device-detect";
import { useAssessmentsByPropertyId } from "./data/hooks/useAssessments";
import { useCreateTag } from "../../lib/tag/hooks/useCreateTag";
import { useDimensions } from "./data/hooks/useDimensions";
import { useFetchAssessmentSchedules } from "../../lib/assessment/schedule/hooks/useFetchAssessmentSchedules";
import { useIssuesByPropertyId } from "./data/hooks/useIssues";
import { useLocations } from "./data/hooks/useLocations";
import { usePageDefaultStyle } from "../../assets/style/usePageDefaultStyle";
import { useProperty } from "./data/hooks/useProperty";
import { useSnackbar } from "notistack";
import { useUpdateAssessment } from "../../lib/assessment/hooks/useUpdateAssessment";
import { useUploadImages } from "../../componentsV2/image/carouselComponent/useUploadImage";
import uuidv4 from "../../lib/util/UuidGenerator";

export const REFETCH_POLLING_TIMES_MS = 60 * 1000;
const locationDAO = GraphQLLocationDAOFactory.getInstance();
const issueDAO = GraphQLIssueDAOFactory.getInstance();
const linkRegistryDAO = GraphQLLinkRegistryDAOFactory.getInstance();
const assessmentSubmissionManager = new AssessmentSubmissionOrchestrator(GraphQLAPI);

type PropertyDashboardProps = {
    readonly propertyId: string;
};

export const PropertyDashboard = (props: PropertyDashboardProps) => {
    const snackbar = useSnackbar();
    const { propertyId } = props;
    const { pageDefaultStyle } = usePageDefaultStyle();
    const { uploadImages } = useUploadImages();
    const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
    const [resourceLoadStartTime] = useState(Date.now());
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

    const queryClient = useQueryClient();

    const { createTagMutation } = useCreateTag(propertyId);
    const { updateAssessmentMutation } = useUpdateAssessment();

    const {
        data: property,
        isLoading: isPropertyQueryLoading,
        isError: isPropertyQueryError
    } = useProperty(propertyId);

    const {
        data: locations,
        isLoading: isLocationsQueryLoading,
        isError: isLocationsQueryError
    } = useLocations(propertyId);

    const {
        data: linkRegistries,
        isLoading: isLinkRegistriesQueryLoading,
        isError: isLinkRegistriesQueryError
    } = useQuery({
        queryKey: ['linkRegistries', propertyId],
        queryFn: async () => {
            return await linkRegistryDAO.listByPropertyId(propertyId);
        },
        enabled: !!propertyId
    });

    const issuesQuery = useIssuesByPropertyId(propertyId);
    const assessmentsQuery = useAssessmentsByPropertyId(propertyId);

    const issues: Array<Issue> = (issuesQuery.data ?? []).filter(issue => !!issue) as Array<Issue>;
    const assessments: Array<Assessment> = (assessmentsQuery.data ?? []).filter(assessment => !!assessment) as Array<Assessment>;
    const imagesQuery = useImagesByPropertyId(propertyId);
    const images: ImageEntity[] = (imagesQuery.data ?? []).filter(image => !!image) as Array<ImageEntity>;

    const {
        data: tags,
        isLoading: isTagsLoading,
        isError: isTagsError
    } = useQuery({
        queryKey: ['tags', propertyId],
        queryFn: async () => graphQLTagDAO.listByPropertyId(propertyId),
        enabled: !!propertyId
    });

    const locationResources = useMemo(() => {
        return locations?.map(location => new Resource(location.id, PermissionResourceType.LOCATION)) ?? [];
    }, [locations]);

    const {
        resourceAssessmentScheduleAssociations,
        assessmentSchedules,
        status: fetchAssessmentScheduleStatus,
        assessmentScheduleIdToResourceIdsMap,
        missedAssessmentNotificationSettings
    } = useFetchAssessmentSchedules(propertyId, locationResources);

    const {
        data: currentUser
    } = useQuery({
        queryKey: ['currentUser'],
        queryFn: async () => {
            const user = await Auth.currentAuthenticatedUser();
            const attributes = await Auth.userAttributes(user);
            const phoneNumber = attributes.find(attr => attr.Name === 'phone_number')?.Value || '';
            const email = attributes.find(attr => attr.Name === 'email')?.Value || '';
            let firstName = attributes.find(attr => attr.Name === 'given_name')?.Value || '';
            let lastName = attributes.find(attr => attr.Name === 'family_name')?.Value || '';
            if (!firstName && !lastName) {
                const name = attributes.find(attr => attr.Name === 'name')?.Value || '';
                const nameParts = name.split(' ');
                firstName = nameParts[0];
                lastName = nameParts.slice(1).join(' ');
            }
            return {
                id: user.username,
                phoneNumber,
                email,
                firstName,
                lastName
            };
        }
    });

    const {
        data: issueSubmissionNotificationSetting,
    } = useQuery({
        queryKey: ['issueSubmissionNotificationSetting', propertyId],
        queryFn: async () => {
            return await graphQLResourceNotificationSettingDAO.get({
                resourceId: propertyId,
                resourceType: NotificationResourceType.PROPERTY,
                notificationType: NotificationType.EXTERNAL_SUBMISSION
            });
        }
    });

    const { propertyDimensionQuery, onCreateDimension, onEditDimensionSubmit } = useDimensions(propertyId);
    const dimensions: Array<Dimension> = (propertyDimensionQuery.data ?? []).filter(dimension => !!dimension) as Array<Dimension>;

    const {
        data: contacts,
        isLoading: isContactsQueryLoading,
        isError: isContactsQueryError
    } = useQuery({
        queryKey: ['contacts'],
        queryFn: async () => {
            return await graphQLContactDAO.list();
        }
    });

    const {
        data: quickQuotes,
        isLoading: isQuickQuotesLoading,
        isError: isQuickQuotesError
    } = useQuery({
        queryKey: ['quickQuotes', propertyId],
        queryFn: async () => {
            return await graphQLQuickQuoteDAO.getByPropertyId(propertyId);
        },
        refetchInterval: REFETCH_POLLING_TIMES_MS,
        refetchIntervalInBackground: true,
        keepPreviousData: true
    });

    const isLoading =
        isPropertyQueryLoading ||
        isLocationsQueryLoading ||
        issuesQuery.isLoading ||
        assessmentsQuery.isLoading ||
        imagesQuery.isLoading ||
        propertyDimensionQuery.isLoading ||
        isLinkRegistriesQueryLoading ||
        isTagsLoading ||
        isContactsQueryLoading ||
        isQuickQuotesLoading;

    const hasError =
        isPropertyQueryError ||
        isLocationsQueryError ||
        issuesQuery.isError ||
        assessmentsQuery.isError ||
        imagesQuery.isError ||
        propertyDimensionQuery.isError ||
        isLinkRegistriesQueryError ||
        isTagsError ||
        isContactsQueryError ||
        isQuickQuotesError;

    const allResourcesLoaded =
        !!property &&
        !!locations &&
        !!issues &&
        !!assessments &&
        !!images &&
        !!dimensions &&
        !!linkRegistries &&
        !!tags &&
        !!contacts &&
        !!resourceAssessmentScheduleAssociations &&
        !!quickQuotes;

    useEffect(() => {
        if (!isLoading && !hasError && allResourcesLoaded) {
            setInitialLoadCompleted(true);
        }
    }, [allResourcesLoaded, isLoading, hasError]);

    useEffect(() => {
        if (initialLoadCompleted) {
            const loadTime = Date.now() - resourceLoadStartTime;
            console.log("loadTime", loadTime);
            awsRum?.recordEvent(
                DATA_LOAD_TIME_METRIC_NAME,
                {
                    loadTimeMs: loadTime,
                    propertyId: propertyId,
                    resourceCounts: {
                        locationsCount: locations?.length ?? 0,
                        issuesCount: issues.length,
                        assessmentsCount: assessments.length,
                        imagesCount: images.length,
                        tagsCount: tags?.length ?? 0,
                        contactsCount: contacts?.length ?? 0,
                        dimensionsCount: dimensions.length,
                        issueSubmissionNotificationSettingCount: issueSubmissionNotificationSetting ? 1 : 0,
                        resourceAssessmentScheduleAssociationsCount: resourceAssessmentScheduleAssociations?.length ?? 0,
                        assessmentSchedulesCount: assessmentSchedules?.length ?? 0,
                        missedAssessmentNotificationSettingsCount: missedAssessmentNotificationSettings ? missedAssessmentNotificationSettings.length : 0,
                        quickQuotesCount: quickQuotes?.length ?? 0
                    }
                }
            );
        }
    }, [initialLoadCompleted])

    const updateIssueSubmissionNotificationSetting = useCallback(async (resourceNotificationSetting: ResourceNotificationSettingDraft) => {
        try {
            if (!issueSubmissionNotificationSetting) {
                await graphQLResourceNotificationSettingDAO.create({
                    resourceId: propertyId,
                    resourceType: NotificationResourceType.PROPERTY,
                    notificationType: NotificationType.EXTERNAL_SUBMISSION,
                    recipients: (resourceNotificationSetting.recipients ?? []) as Array<NotificationRecipient>,
                    enabled: resourceNotificationSetting.enabled
                });
            } else {
                await graphQLResourceNotificationSettingDAO.update({
                    resourceId: propertyId,
                    resourceType: NotificationResourceType.PROPERTY,
                    notificationType: NotificationType.EXTERNAL_SUBMISSION,
                    recipients: (resourceNotificationSetting.recipients ?? []) as Array<NotificationRecipient>,
                    enabled: resourceNotificationSetting.enabled,
                    _version: issueSubmissionNotificationSetting._version
                });
            }
            await queryClient.invalidateQueries({
                queryKey: ["issueSubmissionNotificationSetting", propertyId]
            });
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to update issue submission notification setting", { variant: "error" });
            throw error;
        }
    }, [propertyId, issueSubmissionNotificationSetting]);

    const createAssessmentSchedule = async (
        schedule: AssessmentSchedule,
        selectedLocationIds: Array<string>,
        missedAssessmentNotificationSettings: ResourceNotificationSettingDraft
    ) => {
        try {
            const createdSchedule = await graphQLAssessmentScheduleDAO.create({
                name: schedule.name,
                description: schedule.description,
                startDate: schedule.startDate,
                endDate: schedule.endDate,
                propertyId: propertyId,
                recurrence: schedule.recurrence as RecurrenceInput,
                authorizedIdentifiers: schedule.authorizedIdentifiers,
            });
            await Promise.all(selectedLocationIds.map(async (locationId) => {
                await graphQLResourceAssessmentScheduleAssociationDAO.create({
                    resourceId: locationId,
                    resourceType: PermissionResourceType.LOCATION,
                    assessmentScheduleId: createdSchedule.id
                });
                await queryClient.invalidateQueries(["listAssessmentScheduleAssociationsByResource", locationId]);
            }));
            await graphQLResourceNotificationSettingDAO.create({
                resourceId: createdSchedule.id,
                resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                notificationType: NotificationType.MISSED_ASSESSMENT,
                recipients: (missedAssessmentNotificationSettings.recipients ?? []) as Array<NotificationRecipient>,
                enabled: missedAssessmentNotificationSettings.enabled
            });
            await queryClient.invalidateQueries(["listAssessmentSchedulesByProperty", propertyId]);
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to create assessment schedule", { variant: "error" });
            throw error;
        }
    };

    const createLocation = useCallback(async (name: string, locationImages?: Blob[]) => {
        const location = await locationDAO.create({
            id: uuidv4(),
            name,
            propertyId,
        } as APILocation);
        await queryClient.invalidateQueries(['locations', propertyId]);
        if (!locationImages || locationImages.length === 0) {
            return;
        }
        await uploadImages(
            locationImages,
            { shouldResize: true },
            { id: location.id, type: PermissionResourceType.LOCATION },
            propertyId
        );
    }, [propertyId]);

    const submitIssue = useCallback(async (locationId: string, description: string, images?: File[], measurement?: MeasurementInput) => {
        const submissionImages: Array<StagedImage> = images?.map(image => ({ file: image, id: uuidv4(), imageSource: ImageSource.GALLERY })) ?? [];
        const newIssue: ExternalReportSubmissionIssue = {
            description: description,
            images: submissionImages,
            measurement: measurement
        };

        const externalReportSubmission: ExternalReportSubmission = {
            id: uuidv4(),
            linkRegistrationCreatorId: currentUser?.id,
            propertyId: propertyId,
            locationId: locationId,
            type: ExternalReportType.ISSUE,
            images: [],
            submissionTime: Date.now(),
            issues: [newIssue],
            description: description,
            contactInfo: {
                firstName: currentUser?.firstName,
                lastName: currentUser?.lastName,
                email: currentUser?.email,
                phoneNumber: currentUser?.phoneNumber
            },
        };

        await assessmentSubmissionManager.submitAssessment(externalReportSubmission, submissionImages);
        queryClient.invalidateQueries(['issues', 'property', propertyId]);
        queryClient.invalidateQueries([IMAGE_QUERY_KEY, "property", propertyId]);
    }, [currentUser, propertyId]);

    const editLocation = useCallback(async (locationId: string, name: string) => {
        const existingLocation = locations?.find(location => location.id === locationId);
        if (!existingLocation) {
            return;
        }
        await locationDAO.editLocation(locationId, {
            ...existingLocation,
            name
        });
        await queryClient.invalidateQueries(['locations', propertyId]);
    }, [locations, propertyId]);

    const deleteLocation = useCallback(async (locationId: string) => {
        await locationDAO.deleteLocation(locationId);
        await queryClient.invalidateQueries(['locations', propertyId]);
        const locationScheduleAssociations: Array<ResourceAssessmentScheduleAssociation> = await graphQLResourceAssessmentScheduleAssociationDAO.listByResource(locationId, PermissionResourceType.LOCATION);
        await Promise.allSettled(locationScheduleAssociations.map(association => graphQLResourceAssessmentScheduleAssociationDAO.delete(association.id, (association as any)._version)));
        await queryClient.invalidateQueries({
            queryKey: ["listAssessmentScheduleAssociationsByResource", locationId]
        });
    }, [propertyId]);

    const onEditIssueSubmit = useMutation({
        mutationFn: async (issue: Issue) => {
            return await issueDAO.editIssue(issue.id, issue) as Issue;
        },
        onMutate: async (issue: Issue) => {
            const queryKey = ['issues', 'property', propertyId];
            await queryClient.cancelQueries(queryKey);
            const previousIssues = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (old: Array<Issue> | undefined) => {
                return (old || []).map((oldIssue: Issue) => {
                    if (oldIssue.id === issue.id) {
                        return issue;
                    }
                    return oldIssue;
                });
            });
            return { previousIssues };
        },
        onError: (error, variables, context) => {
            const queryKey = ['issues', 'property', propertyId];
            snackbar.enqueueSnackbar("Failed to edit issue", { variant: "error" });
            if (context?.previousIssues) {
                queryClient.setQueryData(queryKey, context.previousIssues);
            }
        },
        onSettled: (issue) => {
            queryClient.invalidateQueries(['issues', 'property', propertyId]);
        }
    });


    const onContactSubmit = useCallback(async (formData: ContactFormData) => {
        await graphQLContactDAO.create(formData);
        await queryClient.invalidateQueries(['contacts']);
        setShowCreateContactDialog(false);
    }, []);

    const submitQuickQuotes = useCallback(async (quickQuoteInputs: SubmitQuickQuoteInput[]) => {
        snackbar.enqueueSnackbar("Quick estimate started", { variant: "success" });
        try {
            await graphQLQuickQuoteClient.submitQuickQuote(quickQuoteInputs);

            snackbar.enqueueSnackbar("Quick estimate submitted", { variant: "success" });

            const quickQuoteIssues = quickQuoteInputs.map(input => {
                const issueId = input.issueId;
                return issues.find(issue => issue.id === issueId);
            }).filter(issue => !!issue) as Array<Issue>;
            const distinctLocationIds = [...new Set(quickQuoteIssues.map(issue => issue.locationId))];
            distinctLocationIds.forEach(locationId => {
                queryClient.invalidateQueries(['issues', 'property', propertyId]);
            });
            queryClient.invalidateQueries(['quickQuotes', propertyId]);
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to submit Quick estimate. Please try again or contact support", { variant: "error" });
            throw new Error('Failed to submit quick quotes', { cause: error });
        }
    }, [issues]);

    const onEditScheduleSubmit = async (
        schedule: AssessmentSchedule,
        selectedLocationIds: Array<string>,
        missedAssessmentNotificationSettings: ResourceNotificationSettingDraft
    ) => {
        try {
            const associatedLocationIds: Array<string> = assessmentScheduleIdToResourceIdsMap?.get(schedule.id) ?? [];
            const removedLocationIds = associatedLocationIds.filter(locationId => !selectedLocationIds.includes(locationId));
            const newlyAddedLocationIds = selectedLocationIds.filter(locationId => !associatedLocationIds.includes(locationId));

            await Promise.all(removedLocationIds.map(async (locationId) => {
                const associations = await graphQLResourceAssessmentScheduleAssociationDAO.listByScheduleAndResource(schedule.id, locationId);
                if (associations.length === 0) {
                    return;
                }
                await graphQLResourceAssessmentScheduleAssociationDAO.delete(associations[0].id, (associations[0] as any)._version);
                await queryClient.invalidateQueries(["listAssessmentScheduleAssociationsByResource", locationId]);
            }));

            await Promise.all(newlyAddedLocationIds.map(async (locationId) => {
                await graphQLResourceAssessmentScheduleAssociationDAO.create({
                    resourceId: locationId,
                    resourceType: PermissionResourceType.LOCATION,
                    assessmentScheduleId: schedule.id
                });
                await queryClient.invalidateQueries(["listAssessmentScheduleAssociationsByResource", locationId]);
            }));
            await graphQLAssessmentScheduleDAO.update(schedule);
            await queryClient.invalidateQueries(["listAssessmentSchedulesByProperty", propertyId]);
            const existingNotificationSetting = await graphQLResourceNotificationSettingDAO.get({
                resourceId: schedule.id,
                resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                notificationType: NotificationType.MISSED_ASSESSMENT
            });
            if (!existingNotificationSetting) {
                await graphQLResourceNotificationSettingDAO.create({
                    resourceId: schedule.id,
                    resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                    notificationType: NotificationType.MISSED_ASSESSMENT,
                    recipients: (missedAssessmentNotificationSettings.recipients ?? []) as Array<NotificationRecipient>,
                    enabled: missedAssessmentNotificationSettings.enabled
                });
            } else {
                await graphQLResourceNotificationSettingDAO.update({
                    resourceId: schedule.id,
                    resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                    notificationType: NotificationType.MISSED_ASSESSMENT,
                    recipients: (missedAssessmentNotificationSettings.recipients ?? []) as Array<NotificationRecipient>,
                    enabled: missedAssessmentNotificationSettings.enabled,
                    _version: (missedAssessmentNotificationSettings as any)._version
                });
            }
            await queryClient.invalidateQueries({
                queryKey: ["getMissedAssessmentNotificationSetting", schedule.id]
            });
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to update assessment schedule", { variant: "error" });
            throw error;
        }
    };

    const onDeleteAssessmentSchedule = async (assessmentSchedule: AssessmentSchedule, associatedLocations: Location[]) => {
        try {
            const associationsToRemove = (await Promise.all(associatedLocations.map(async (location) => {
                const associations = await graphQLResourceAssessmentScheduleAssociationDAO.listByScheduleAndResource(assessmentSchedule.id, location.id);
                return associations;
            }))).flat();
            await Promise.all(associationsToRemove.map(async (association) => {
                await graphQLResourceAssessmentScheduleAssociationDAO.delete(association.id, (association as any)._version);
                await queryClient.invalidateQueries(["listAssessmentScheduleAssociationsByResource", association.resourceId]);
            }));
            await graphQLAssessmentScheduleDAO.delete(assessmentSchedule.id, (assessmentSchedule as any)._version);
            await queryClient.invalidateQueries(["listAssessmentSchedulesByProperty", propertyId]);
            const existingNotificationSetting = await graphQLResourceNotificationSettingDAO.get({
                resourceId: assessmentSchedule.id,
                resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                notificationType: NotificationType.MISSED_ASSESSMENT
            });
            if (existingNotificationSetting) {
                await graphQLResourceNotificationSettingDAO.delete({
                    resourceId: assessmentSchedule.id,
                    resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
                    notificationType: NotificationType.MISSED_ASSESSMENT,
                    _version: existingNotificationSetting._version
                });
            }
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to delete assessment schedule", { variant: "error" });
        }
    };

    if (!initialLoadCompleted || isLoading) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap={1}
                sx={pageDefaultStyle}
            >
                <Typography variant="body2">
                    Loading property data...
                </Typography>
                <Box width="100%">
                    <LinearProgress />
                </Box>
            </Box>
        );
    }

    if (!initialLoadCompleted && hasError) {
        throw new Error('Error loading property data');
    }

    return (
        allResourcesLoaded ? <>
            <Box
                sx={pageDefaultStyle}
            >
                {isMobile && (<Typography variant="h2">{property.name}</Typography>)}
                {!isMobile && (
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        pt={4}
                        px={{ md: 6 }}
                    >
                        <Typography variant="h2">{property.name}</Typography>
                    </Box>
                )}
                <TeneraPropertyDashboard
                    property={property as APIProperty}
                    locations={locations as Array<APILocation>}
                    linkRegistries={linkRegistries as Array<APILinkRegistry>}
                    issues={issues as Array<APIIssue>}
                    images={images}
                    assessmentSchedules={assessmentSchedules as Array<APIAssessmentSchedule>}
                    assessments={assessments as Array<APIAssessment>}
                    currentUserId={currentUser?.id}
                    resourceAssessmentScheduleAssociations={resourceAssessmentScheduleAssociations as Array<APIResourceAssessmentScheduleAssociation>}
                    missedAssessmentNotificationSettings={missedAssessmentNotificationSettings}
                    issueSubmissionNotificationSetting={issueSubmissionNotificationSetting}
                    updateIssueSubmissionNotificationSetting={updateIssueSubmissionNotificationSetting}
                    submitIssue={submitIssue}
                    createLocation={createLocation}
                    editLocation={editLocation}
                    deleteLocation={deleteLocation}
                    fetchAssessmentScheduleStatus={fetchAssessmentScheduleStatus}
                    createAssessmentSchedule={createAssessmentSchedule}
                    onEditScheduleSubmit={onEditScheduleSubmit}
                    onDeleteAssessmentSchedule={onDeleteAssessmentSchedule}
                    onEditIssueSubmit={async (id, issue) => { await onEditIssueSubmit.mutateAsync(issue); }}
                    contacts={contacts}
                    setIsOpenCreateContactDialog={setShowCreateContactDialog}
                    tags={(tags ?? []) as Array<Tag>}
                    createTag={(tagName) => createTagMutation.mutateAsync({
                        name: tagName,
                        propertyId
                    }) as Promise<Tag>}
                    submitQuickQuotes={submitQuickQuotes}
                    onEditAssessmentSubmit={async (assessmentId, assessment) => {
                        if (!assessment) {
                            return;
                        }
                        await updateAssessmentMutation.mutateAsync(assessment);
                    }}
                    dimensions={dimensions as Dimension[]}
                    onEditDimensionSubmit={onEditDimensionSubmit.mutateAsync}
                    onCreateDimension={onCreateDimension.mutateAsync}
                    quickQuotes={quickQuotes}
                />
            </Box>
            <ContactFormDialog
                isOpen={showCreateContactDialog}
                setIsOpen={setShowCreateContactDialog}
                type="add"
                onSubmit={onContactSubmit}
            />
        </> : <></>
    );
};